import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCapabilities } from '../context/CapabilitiesContext';
import { latestRelease } from '../content/latestRelease';
import {
  User,
  ChevronDown,
  ChevronRight,
  Info,
  Stethoscope,
  UserRound,
  Bot,
  Lock,
  LogOut,
  Activity,
} from 'lucide-react';

const Home = () => {
  const { user, hasConsent, giveConsent, withdrawConsent, consentLoading, signOut } = useAuth();
  const { loading: capabilitiesLoading, error: capabilitiesError, isModuleEnabled } = useCapabilities();
  const [showDetails, setShowDetails] = useState(false);
  const [showLatestUpdate, setShowLatestUpdate] = useState(false);
  const navigate = useNavigate();

  const handleConsentChange = async (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      await giveConsent();
    } else {
      await withdrawConsent();
    }
  };

  const handleModuleClick = (path, requiresConsent = true) => {
    if (requiresConsent && !hasConsent) {
      showConsentModal();
      return;
    }
    navigate(path);
  };

  const showConsentModal = () => {
    const modal = document.createElement('div');
    modal.className = 'consent-modal';
    modal.innerHTML = `
      <div class="consent-modal-content">
        <div class="consent-modal-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
        <p>Enable by granting consent above</p>
      </div>
    `;

    document.body.appendChild(modal);

    setTimeout(() => {
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }
    }, 1000);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const modules = [
    {
      id: 'doctor',
      path: '/doctor',
      icon: <Stethoscope size={28} />,
      title: 'Doctor Simulator',
      description: 'Train diagnostic skills with AI patients',
      longDescription:
        'Practice medical consultation and diagnostic reasoning with realistic AI-powered patient simulations.',
      requiresConsent: true,
      moduleKey: 'module_1',
    },
    {
      id: 'patient',
      path: '/patient',
      icon: <UserRound size={28} />,
      title: 'Patient Simulator',
      description: 'Practice patient interaction scenarios',
      longDescription:
        'Experience realistic patient interactions from the patient perspective to improve communication skills.',
      requiresConsent: true,
      moduleKey: 'module_2',
      comingSoon: true,
    },
    {
      id: 'copilot',
      path: '/copilot',
      icon: <Bot size={28} />,
      title: 'Medical Copilot',
      description: 'Get AI assistance with clinical cases',
      longDescription:
        'Receive evidence-based insights and recommendations for complex clinical decision making.',
      requiresConsent: true,
      moduleKey: 'module_3',
      comingSoon: true,
    },
  ];

  const getModuleState = (module) => {
    const comingSoon = Boolean(module.comingSoon);
    const capabilityLoading = !comingSoon && capabilitiesLoading;
    const capabilityUnavailable =
      !comingSoon &&
      !capabilityLoading &&
      (capabilitiesError || !isModuleEnabled(module.moduleKey));
    const consentRestricted =
      !comingSoon && !capabilityUnavailable && module.requiresConsent && !hasConsent;

    return {
      comingSoon,
      capabilityLoading,
      capabilityUnavailable,
      consentRestricted,
      disabled: comingSoon || capabilityLoading || capabilityUnavailable || consentRestricted,
    };
  };

  const getModuleAction = (module, state) => {
    if (state.comingSoon) {
      return 'Coming Soon';
    }

    if (state.consentRestricted) {
      return (
        <>
          <Lock size={14} />
          Enable by granting consent above
        </>
      );
    }

    if (state.capabilityLoading) {
      return 'Loading...';
    }

    if (state.capabilityUnavailable) {
      return 'Unavailable';
    }

    return module.id === 'doctor' ? 'Open Workspace' : 'Start Session';
  };

  return (
    <div className="home-page">
      <div className="home-container">
        {/* Header */}
        <div className="home-header">
          {/* Profile Section */}
          <div className="profile-section">
            <div className="profile-placeholder">
              <div className="profile-avatar">
                <User size={18} />
              </div>
              <div className="profile-info">
                <span className="profile-name">
                  {user ? (user.user_metadata?.full_name || user.email) : 'Guest User'}
                </span>
                <div className="profile-dropdown">
                  <button className="dropdown-toggle">
                    <ChevronDown size={14} />
                  </button>
                  <div className="dropdown-menu">
                    <button className="dropdown-item" onClick={handleLogout}>
                      <LogOut size={14} />
                      Log Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="main-logo-section">
            <img
              src="/ApolloLogo.png"
              alt="Apollo Medical AI Logo"
              className="main-apollo-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="main-fallback-logo" style={{ display: 'none' }}>
              <Activity size={32} />
            </div>
          </div>

          <div className="header-text">
            <h1>Apollo Medical AI</h1>
            <p className="subtitle">Advanced Medical Training Simulation Platform</p>
          </div>
        </div>

        {/* Latest Update Section */}
        <section className="latest-update-section" aria-labelledby="latest-update-heading">
          <div className={`latest-update-card ${showLatestUpdate ? 'expanded' : ''}`}>
            <h2 id="latest-update-heading" className="latest-update-title">
              <button
                type="button"
                className="latest-update-toggle"
                onClick={() => setShowLatestUpdate((current) => !current)}
                aria-expanded={showLatestUpdate}
                aria-controls="latest-update-content"
              >
                <span className="latest-update-header">
                  <span>{latestRelease.title}</span>
                  <span className="latest-update-date">{latestRelease.date}</span>
                </span>
                <ChevronDown
                  className={`latest-update-chevron ${showLatestUpdate ? 'expanded' : ''}`}
                  size={18}
                  aria-hidden="true"
                />
              </button>
            </h2>

            {showLatestUpdate && (
              <div id="latest-update-content" className="latest-update-content">
                <p className="latest-update-summary">{latestRelease.summary}</p>
                <ul className="latest-update-highlights">
                  {latestRelease.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Consent Section */}
        <div className="consent-section">
          <div className="consent-card">
            <div className="consent-header">
              <span className="info-icon">
                <Info size={18} />
              </span>
              <h2>Data Collection Consent</h2>
            </div>

            <div className="consent-info">
              <p>
                Educational use only. With your consent, we collect the text you enter in modules to
                improve reliability and safety. Do <strong>not</strong> include personal identifiers.
              </p>
            </div>

            <div className="consent-details">
              <button
                className="details-toggle"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                View details
              </button>

              {showDetails && (
                <div className="details-content">
                  <h4>What is collected.</h4>
                  <p>
                    Prompts you enter, AI responses, timestamps, and basic non-identifying technical metadata.
                  </p>

                  <h4>Identity.</h4>
                  <p>
                    We collect your Google account email for access control and usage analytics.
                    This is stored separately from transcripts and used only for research and product
                    improvement.
                  </p>

                  <h4>Purpose.</h4>
                  <p>Debugging, quality measurement, and improving educational safety.</p>

                  <h4>What not to enter.</h4>
                  <p>Names, contacts, IDs, addresses, or any personally identifying information.</p>

                  <h4>Consent & control.</h4>
                  <p>
                    By checking the box below, you consent to this limited data use. You may withdraw
                    consent at any time from this page; withdrawal stops future collection. For
                    deletion requests, contact the data controller at e0959157@u.nus.edu.
                  </p>
                </div>
              )}
            </div>

            <div className="consent-checkbox">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={hasConsent}
                  onChange={handleConsentChange}
                  disabled={consentLoading}
                />
                <span className="checkmark"></span>
                <span className="checkbox-text">
                  {consentLoading
                    ? 'Processing...'
                    : 'I agree to the data use described above.'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Module Cards */}
        <div className="modules-section">
          <h2>Choose Your Module</h2>
          {capabilitiesLoading && (
            <div className="consent-required-container">
              <div className="loading-spinner">Loading module availability...</div>
            </div>
          )}
          {capabilitiesError && (
            <div className="consent-required-container">
              <h3>Configuration unavailable</h3>
              <p>Module availability could not be loaded. Please try again later.</p>
            </div>
          )}
          <div className="modules-grid">
            {modules.map((module) => {
              const state = getModuleState(module);

              return (
                <div
                  key={module.id}
                  className={`module-card ${
                    state.consentRestricted || state.capabilityUnavailable ? 'disabled' : ''
                  } ${state.comingSoon ? 'coming-soon' : ''}`}
                >
                  <div className="module-icon">{module.icon}</div>
                  <h3>{module.title}</h3>
                  <p className="module-description">{module.description}</p>
                  <p className="module-long-description">{module.longDescription}</p>

                  <button
                    className={`module-button ${
                      state.disabled ? 'disabled' : ''
                    } ${state.comingSoon ? 'coming-soon' : ''}`}
                    onClick={() =>
                      !state.disabled && handleModuleClick(module.path, module.requiresConsent)
                    }
                    disabled={state.disabled}
                  >
                    {getModuleAction(module, state)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="home-footer">
          <p>
            Built with <strong>React</strong>, <strong>FastAPI</strong> &{' '}
            <strong>OpenAI API</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Home;
